import type { GetProductBillingCancellationOutput } from "@redotech/admin-sdk/rpc/schema/billing/product-billing-cancellation";
import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { RedoRadioGroup } from "@redotech/arbiter/radio/redo-radio-group";
import { Text } from "@redotech/arbiter/text";
import { BillingCancellationMode } from "@redotech/crdb-enums/billing/billing-cancellation-mode";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  allowedCancellationModes,
  defaultCancellationMode,
  resolveBillableUntil,
} from "@redotech/redo-billing-common/billing-cancellation";
import { ServiceInvoiceType } from "@redotech/redo-billing-common/service-invoice";
import { toast } from "@redotech/redo-web/alert";
import { DateInput } from "@redotech/redo-web/date-picker";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { getDateStartOfDayInTimezone } from "@redotech/util/date";
import { assertNever } from "@redotech/util/type";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

type BillingCancellationState = NonNullable<
  GetProductBillingCancellationOutput["state"]
>;

function modeLabel(mode: BillingCancellationMode): string {
  switch (mode) {
    case BillingCancellationMode.Immediate:
      return "Stop billing usage now";
    case BillingCancellationMode.EndOfPeriod:
      return "Bill through the end of this period";
    case BillingCancellationMode.EndOfNextPeriod:
      return "Bill through the end of next period";
    case BillingCancellationMode.Custom:
      return "Stop billing usage on a date";
    default:
      return assertNever(mode);
  }
}

function formatDate(date: Date, timeZone: string): string {
  return date.toLocaleString(undefined, {
    timeZone,
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Skips loading while `teamId` is undefined. */
export function useBillingCancellationState(
  teamId: string | undefined,
  productType: ServiceInvoiceType,
) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [reloadKey, setReloadKey] = useState(0);
  const load = useLoad(
    async () =>
      teamId
        ? await rpcClient.getProductBillingCancellation({ teamId, productType })
        : null,
    [rpcClient, teamId, productType, reloadKey],
  );
  return {
    state: load.value?.state ?? undefined,
    reload: () => setReloadKey((key) => key + 1),
  };
}

/** Picks when billing ends and spells out what gets billed before recording it. */
export const CancelBillingModal = memo(function CancelBillingModal({
  teamId,
  productType,
  teamTimezone,
  state,
  onClose,
  onCancelled,
}: {
  teamId: string;
  productType: ServiceInvoiceType;
  teamTimezone: string;
  state: BillingCancellationState;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [mode, setMode] = useState(defaultCancellationMode(state.policy));
  const [customDate, setCustomDate] = useState<Date | null>(null);
  const [now] = useState(() => new Date());

  const period = state.period;
  const customCutoff = customDate
    ? getDateStartOfDayInTimezone(customDate, teamTimezone)
    : undefined;

  let billableUntil: Date | null = null;
  let invalidReason: string | null = null;
  if (period) {
    try {
      billableUntil = resolveBillableUntil({
        mode,
        now,
        periodStart: period.start,
        periodEnd: period.end,
        nextPeriodEnd: period.nextEnd,
        customDate: customCutoff,
      });
    } catch (error) {
      invalidReason = errorMessage(error);
    }
  }
  const irreversible = !!billableUntil && billableUntil <= now;

  const confirm = useHandler(async () => {
    try {
      await rpcClient.cancelProductBilling({
        teamId,
        productType,
        mode,
        customDate: customCutoff ?? null,
      });
      onCancelled();
    } catch (error) {
      toast(errorMessage(error), { variant: "error" });
    }
  });

  const modes = allowedCancellationModes(state.policy);

  return (
    <RedoModal
      isOpen
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Cancel billing",
        theme: "destructive",
        onClick: confirm,
        disabled: !billableUntil,
      }}
      secondaryButton={{ text: "Keep billing", onClick: onClose }}
      title="Cancel billing"
    >
      <Flex dir="column" gap="lg">
        {period && (
          <Text fontSize="sm" textColor="secondary">
            Current period: {formatDate(period.start, teamTimezone)} –{" "}
            {formatDate(period.end, teamTimezone)}
          </Text>
        )}
        {modes.length > 1 && (
          <RedoRadioGroup<BillingCancellationMode>
            label="When billing ends"
            options={modes.map((value) => ({
              id: value,
              label: modeLabel(value),
            }))}
            setValue={setMode}
            value={mode}
          />
        )}
        {mode === BillingCancellationMode.Custom && (
          <LabeledInput label="Stop billing usage on">
            <DateInput value={customDate} valueChange={setCustomDate} />
          </LabeledInput>
        )}
        {billableUntil && (
          <Text fontSize="sm">
            Usage after {formatDate(billableUntil, teamTimezone)} won't be
            billed. The product stays on.
            {irreversible && " This can't be undone."}
          </Text>
        )}
        {invalidReason && customDate && (
          <Text fontSize="sm" textColor="error">
            {invalidReason}
          </Text>
        )}
      </Flex>
    </RedoModal>
  );
});

/** Shows a product's billing cancellation, or a button to cancel. */
export const ProductBillingCancellation = memo(
  function ProductBillingCancellation({
    teamId,
    productType,
    teamTimezone,
    disabled,
    state,
    reload,
    onRenewalResumed,
  }: {
    teamId: string;
    productType: ServiceInvoiceType;
    teamTimezone: string;
    disabled: boolean;
    state: BillingCancellationState | undefined;
    reload: () => void;
    /** Undo turned renewal back on; keep any open form in sync. */
    onRenewalResumed?: () => void;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const [modalOpen, setModalOpen] = useState(false);
    const [undoing, setUndoing] = useState(false);

    const undo = useHandler(async () => {
      setUndoing(true);
      try {
        const { renewalResumed } =
          await rpcClient.revokeProductBillingCancellation({
            teamId,
            productType,
          });
        if (renewalResumed) {
          onRenewalResumed?.();
        } else {
          toast(
            "Cancellation undone. Turn renewal back on to keep billing this product.",
            { variant: "info" },
          );
        }
        reload();
      } catch (error) {
        toast(errorMessage(error), { variant: "error" });
      } finally {
        setUndoing(false);
      }
    });

    if (!state?.period) {
      return null;
    }

    if (state.cancellation) {
      const { cancellation } = state;
      const stopped = cancellation.postCancelledHookRanAt !== null;
      return (
        <Flex dir="column" gap="md">
          <Text fontWeight="semibold">Billing cancelled</Text>
          <Text>
            {stopped
              ? "Usage stopped being billed"
              : "Usage stops being billed"}{" "}
            {formatDate(cancellation.billableUntil, teamTimezone)} (
            {modeLabel(cancellation.mode).toLowerCase()}). The product stays on;
            usage after that time is free. Cancelled by{" "}
            {cancellation.cancelledBy} on{" "}
            {formatDate(cancellation.cancelledAt, teamTimezone)}.
          </Text>
          {!stopped && (
            <div>
              <RedoButton
                disabled={disabled || undoing}
                hierarchy="secondary"
                onClick={undo}
                size="sm"
                text="Undo cancellation"
              />
            </div>
          )}
        </Flex>
      );
    }

    return (
      <>
        <div>
          <RedoButton
            disabled={disabled}
            hierarchy="secondary"
            onClick={() => setModalOpen(true)}
            size="sm"
            text="Cancel billing…"
          />
        </div>
        {modalOpen && (
          <CancelBillingModal
            onCancelled={() => {
              setModalOpen(false);
              reload();
            }}
            onClose={() => setModalOpen(false)}
            productType={productType}
            state={state}
            teamId={teamId}
            teamTimezone={teamTimezone}
          />
        )}
      </>
    );
  },
);
